import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { apiErrorMessage } from '../lib/apiError';

const CreateOrganizationPage = () => {
  const [orgName, setOrgName] = useState('');
  const [adminName, setAdminName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { createOrganization } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setIsLoading(true);

    try {
      await createOrganization(orgName, adminName, email, password);
      toast.success('Organization created successfully!');
      navigate('/admin', { replace: true });
    } catch (error) {
      setError(apiErrorMessage(error, 'Failed to create organization. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
          Create your organization
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Set up your company workspace and admin account
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4 bg-red-50 border-red-200">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-600 text-sm">
            {error}
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="orgName" className="text-sm font-medium text-slate-900">
            Organization Name
          </Label>
          <Input
            id="orgName"
            type="text"
            placeholder="Acme Construction Inc."
            value={orgName}
            onChange={(e) => { setOrgName(e.target.value); setError(''); }}
            required
            disabled={isLoading}
            className="h-10"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="adminName" className="text-sm font-medium text-slate-900">
            Your Name
          </Label>
          <Input
            id="adminName"
            type="text"
            placeholder="John Smith"
            value={adminName}
            onChange={(e) => { setAdminName(e.target.value); setError(''); }}
            required
            disabled={isLoading}
            className="h-10"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-slate-900">
            Company Email
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="admin@company.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(''); }}
            required
            disabled={isLoading}
            className="h-10"
            autoComplete="email"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium text-slate-900">
            Password
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Min. 8 characters"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              required
              disabled={isLoading}
              className="h-10 pr-10"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-900">
            Confirm Password
          </Label>
          <Input
            id="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            placeholder="Confirm your password"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
            required
            disabled={isLoading}
            className="h-10"
            autoComplete="new-password"
          />
        </div>

        <Button
          type="submit"
          className="w-full h-10 bg-slate-900 text-white hover:bg-slate-800 font-medium"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating organization...
            </>
          ) : (
            'Create organization'
          )}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-slate-500">
        Already have an account?{' '}
        <Link
          to="/login"
          className="font-medium text-slate-900 hover:underline underline-offset-4"
        >
          Sign in
        </Link>
      </p>
    </div>
  );
};

export default CreateOrganizationPage;
